import {useProjectPickerQuery} from '../../generated/graphql';
import {useViewer} from '../../state/viewer/hooks';
import {propagateErrorsContext} from '../../util/errors';
import {type Project} from './types';

const OPTIONS_COUNT = 100;

type UseProjectPickerOptionsOpts = {
  entityName?: string;
  exclude: Project[];
  organizationId?: string;
  pattern?: string;
  requireSameEntity?: boolean;
  selectedProject: Project | null;
};

export type ProjectPickerScope = 'entity' | 'none' | 'organization';

export function getProjectPickerScope({
  entityName,
  organizationId,
  username,
}: {
  entityName?: string;
  organizationId?: string;
  username?: string;
}): ProjectPickerScope {
  if (organizationId != null) {
    return 'organization';
  }

  if (entityName != null && username != null) {
    return 'entity';
  }

  return 'none';
}

export function useProjectPickerOptions({
  entityName,
  exclude,
  organizationId,
  pattern,
  requireSameEntity = false,
  selectedProject,
}: UseProjectPickerOptionsOpts) {
  const viewer = useViewer();
  const scope = getProjectPickerScope({
    entityName,
    organizationId,
    username: viewer?.username,
  });

  const result = useProjectPickerQuery({
    context: propagateErrorsContext(),
    skip: scope === 'none',
    variables: {
      entityName: entityName ?? '',
      includeViewerAndTeamProjects: scope === 'entity' && !requireSameEntity,
      organizationId: organizationId ?? '',
      pattern,
      skipEntity: scope !== 'entity',
      skipOrganization: scope !== 'organization',
      userName: viewer?.username ?? '',
    },
  });

  const entityProjects =
    result.data?.entity?.projects?.edges.map(e => e.node) ?? [];
  const viewerProjects =
    result.data?.viewer?.projects?.edges.map(e => e.node) ?? [];
  const userProjects =
    result.data?.user?.teams?.edges.flatMap(edge =>
      edge.node?.projects?.edges.map(e => e.node)
    ) ?? [];
  const organizationProjects =
    result.data?.organization?.projects?.edges.map(e => e.node) ?? [];

  const projects: Project[] = [];
  const seenProjectIds = new Set<string>(exclude.map(e => e.internalId));
  if (selectedProject != null) {
    seenProjectIds.add(selectedProject.internalId);
  }

  // Organization scope uses organization projects directly. Entity scope
  // combines entity, viewer, and team projects and removes duplicates.
  const sourceProjects =
    scope === 'organization'
      ? organizationProjects
      : [...userProjects, ...viewerProjects, ...entityProjects];
  for (const project of sourceProjects) {
    if (project == null || seenProjectIds.has(project.internalId)) {
      continue;
    }
    seenProjectIds.add(project.internalId);
    projects.push(project);
  }

  projects.sort((a, b) => {
    const entityComp = a.entityName.localeCompare(b.entityName);
    const nameComp = a.name.localeCompare(b.name);

    return entityComp || nameComp;
  });
  projects.splice(OPTIONS_COUNT, projects.length);

  if (selectedProject != null) {
    projects.unshift(selectedProject);
  }

  return {...result, data: projects};
}
